import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '@/services/notificationService';
import { toast } from '@/hooks/use-toast';
import { queryKeys } from '@/lib/queryKeys';

export const useNotifications = (options?: { read?: boolean; limit?: number }) => {
  return useQuery({
    queryKey: queryKeys.notifications.list(options?.read, options?.limit),
    queryFn: () => notificationService.getAll(options),
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useUnreadNotificationCount = () => {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationService.markAsRead,
    onSuccess: () => {
      // Selective invalidation: only notification queries
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.notifications.all,
        exact: false,
      });
    },
  });
};

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationService.markAllAsRead,
    onSuccess: () => {
      // Selective invalidation
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.notifications.all,
        exact: false,
      });
      toast({
        title: 'All notifications marked as read',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message,
      });
    },
  });
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationService.delete,
    onSuccess: () => {
      // Selective invalidation - silent success, visual removal is sufficient
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.notifications.all,
        exact: false,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message,
      });
    },
  });
};

export const useNotificationPreferences = () => {
  return useQuery({
    queryKey: queryKeys.notifications.preferences(),
    queryFn: () => notificationService.getPreferences(),
  });
};

export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationService.updatePreferences,
    onSuccess: () => {
      // Selective invalidation - silent success for settings changes
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.notifications.preferences(),
        exact: true,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message,
      });
    },
  });
};
